/**
 * Section 06 — Charts.
 *
 * The biggest section. Charts is the most feature-dense page in the app,
 * so this walks every major mechanic top-to-bottom: layouts, intervals,
 * the Chart Settings dialog, indicator overlays (categorised), order
 * capture flow (nBUY/nSHORT/bBUY/bSHORT + OrderConfirmDialog), the
 * Order Flow ribbon, HVN levels, Bar Replay mode, and FNO overlays.
 */
import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, List, ListItem, ListItemText } from '@mui/material';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import GuideKeycap from '../GuideKeycap';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const H3 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2.5, mb: 0.75 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_charts: GuideSection = {
  id: 'charts',
  label: 'Charts',
  group: 'pages',
  icon: <CandlestickChartIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Charts</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        The most powerful page. Reading prices, applying indicators, capturing entry/exit levels, and
        replaying historical bars to validate setups. This section is long because the page does a lot —
        skim the headings + read the parts you need.
      </Typography>

      <H id="layouts">Layouts — single / 2-split / 4-quad / workspace</H>
      <P>
        Use the layout switcher in the top toolbar to choose how many charts share the screen.
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Single</strong> — one chart filling the page. Best for deep-dive on a single instrument.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>2-split</strong> — two charts side by side (horizontal) or stacked (vertical). Useful for comparing same symbol across two intervals, or two related symbols.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>4-quad (Options)</strong> — 2×2 grid for an option chain: spot, two option-leg charts with their own strike/expiry, and the combined premium.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Options Workspace</strong> — Delta BTC/ETH option chain, basket builder and payoff analysis on one screen, with the spot chart as a strip on top. Places PAPER baskets. See the <em>Options Workspace</em> guide section.</>} />
        </ListItem>
      </List>
      <P>
        Each pane is independent — different symbol, different interval, different overlays. Replay mode
        syncs the playhead across all panes (R68b).
      </P>

      <H id="intervals">Intervals</H>
      <P>
        The interval segmented control sets the candle period. Supported:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Intraday</strong>: 1m, 3m, 5m, 15m, 30m, 1h, 2h, 4h</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Daily +</strong>: 1d, 1w</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Composite (non-standard)</strong>: 45m, 90m — aggregated server-side from 5/15 min bars.</>} />
        </ListItem>
      </List>
      <P>
        On every interval change the chart refetches up to 1000 bars + recomputes every active overlay.
        CMid + multi-timeframe overlays may take an extra ~500 ms because they additionally fetch the
        CTF and HTF candles.
      </P>

      <H id="chart-settings">Chart Settings dialog (5 tabs)</H>
      <P>
        Click the gear icon in the toolbar to open Chart Settings. Five tabs group all knobs; each section is
        collapsible. Edits to an existing template <strong>auto-save ~600 ms</strong> after your last change (a{' '}
        <em> ✓ Saved</em> / <em>Saving…</em> indicator sits by the Save button), and you can drag the dialog by its
        title bar to reposition it.
      </P>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Tab</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Controls</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell><strong>Candles</strong></TableCell><TableCell>Rising / falling colours; <em>Reset remembered zoom</em>; live-update poll cadence; BBR &amp; Volume-spike highlight colours; grid intensity. LR body (CLN) regression length, its thin-volume outline, and whether the LR chip ghosts the candles. The <em>Important fraction</em> (candles from the last part of each bar). The <em>P-Zone</em> distances, colour and fill. (Candle <em>type</em> and <em>wick</em> on/off are quick toggles on the toolbar, not here.)</TableCell></TableRow>
          <TableRow><TableCell><strong>Lines</strong></TableCell><TableCell>LTP line, Delta mark-price line, and session-start lines (style, colour, visibility).</TableCell></TableRow>
          <TableRow><TableCell><strong>Bands &amp; Clouds</strong></TableCell><TableCell>CPR, POC (C-POC + H-POC), LR_HAMA_Band, AvgCloud, and Combined Midline (CMid).</TableCell></TableRow>
          <TableRow><TableCell><strong>Indicators</strong></TableCell><TableCell>EMAs, RelStrength, ChgN%, MACD, JHM, WR%, Vol, AV_Band, Trend, OverView (the per-constituent Contributors panel), Bkground, Funding, BOLev, FBO, Wave, and the Order Flow ribbon.</TableCell></TableRow>
          <TableRow><TableCell><strong>General</strong></TableCell><TableCell>Chart-loading / remembered-setup behaviour and other page-wide preferences.</TableCell></TableRow>
        </TableBody>
      </Table>

      <H id="overlay-categories">Indicator overlays — categorised</H>
      <P>
        Every overlay is a checkbox in Chart Settings → Overlays. See <OpenInApp to="/app-guide#indicators" label="Indicators Reference" />
        for formula + worked-example per indicator. Here's the high-level grouping:
      </P>
      <H3>Trend</H3>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>EMAs</strong> — four configurable periods, each toggle-able. Default <strong>7 / 21 / 51 / 200</strong>, computed over <strong>LinReg(close, 14)</strong> (not the raw close); each renders in a distinct colour + progressively thicker stroke.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>AvgCloud</strong> — trend cloud anchored on CMid (R72). Green = uptrend, red = downtrend.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>CMid</strong> — Combined Midline, 3-TF Bollinger blend. The reference line that drives the AvgCloud direction (and RockBand's regime read).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>LR_HAMA Band</strong> — Linear-Regression Heikin-Ashi MA envelope; used by DailyBlast.</>} />
        </ListItem>
      </List>

      <H3>Volatility</H3>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Bollinger Band</strong> — SMA ± kσ. Configurable period and k.</>} />
        </ListItem>
      </List>

      <H3>Volume</H3>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>C-POC</strong> — Current Point of Control, the price with the most session volume.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>HVN levels</strong> — your saved high-volume nodes drawn across the chart (also mirrored to Price Level Watch with cross alerts — see below).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Vol</strong> — Weighted Volume (volume × close): lower-pane spike bars + Fast/Slow EMAs with a bull/bear band. Shares one chip with Flow.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>AV_Band</strong> — Anchored Volume Band: HMAs of an OverView-steered "Effective Line". Choose a <em>staircase</em> or <em>smooth line</em> style in Chart Settings → Indicators → AV_Band.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Flow ribbon</strong> — bottom histogram, taker-buy vs taker-sell volume per candle.</>} />
        </ListItem>
      </List>

      <H3>Reference lines</H3>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>CPR</strong> — Central Pivot Range from yesterday's H/L/C.</>} />
        </ListItem>
      </List>

      <H3>Comparison</H3>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>RelStrength</strong> — ratio of chart symbol vs benchmark LinReg(14). Own pane. Directional band: green when the RS line is above its EMA, red below (colors/opacity in Chart Settings).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>ChgN%</strong> — rolling N-bar % change with EMA. Own pane. Directional band: green when the line is above its EMA, red below.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>MACD</strong> — EMA(12)−EMA(26) on the CSH-smoothed close (always, regardless of the chart's candle type) with an SMA(9) signal. MACD + Signal lines with a band colored by the MACD line's slope: green while MACD is rising, red while falling. Fast/Slow/Signal lengths, line + band colors and opacity are all in Chart Settings → Indicators → MACD. Own pane.</>} />
        </ListItem>
      </List>

      <H id="order-capture">Order capture — two distinct tools</H>
      <P>
        The chart toolbar gives you two separate ways to act on a level. Don't confuse them — they feed
        different Dashboard panels:
      </P>
      <H3>1 · BUY / SHORT — deferred entries</H3>
      <P>
        The green <strong>BUY</strong> and red <strong>SHORT</strong> buttons capture an actual entry. Click
        the button, then click the chart to set your level(s) in the order <strong>Entry → Stop-Loss →
        Target</strong> (with multi-entry scale-in you click each of the N entries first — see below). The
        OrderConfirmDialog then opens showing the <strong>auto-computed quantity</strong> (editable), the
        order type for Entry-1 (<em>LIMIT</em> rests / <em>MARKET</em> fires now), and the Risk:Reward — you
        set SL and Target by clicking them on the chart, they are not auto-guessed. Confirm to place. These
        rest locally (the exchange never sees the level) and appear in the Dashboard's <strong>Pending Manual
        Entry Watch</strong>, where each is auto-classified DIP or STOP by which side of the live price it sits.
      </P>
      <P>
        <span id="anchor-paper-trade" style={{ scrollMarginTop: 80 }} />
        <strong>LIVE or PAPER.</strong> The dialog&apos;s <strong>Place as</strong> row chooses how the order is placed.
        It always opens on <strong>LIVE (real orders)</strong> and the button reads <em>Place LIVE BUY …</em> in red.
        Pick <strong>PAPER (simulated)</strong> and it becomes a grey <em>Place PAPER BUY …</em>: the same trade is
        simulated by the app instead — no broker order, no API key needed for Delta, no margin used.
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Fills</strong> — MARKET fills at the live price when you confirm. A LIMIT entry
            rests in Pending Manual Entry Watch (with a PAPER chip) under the same DIP / STOP, expiry and
            &quot;price already past the stop&quot; rules, and fills at the live price on the check where price reaches it.
            Scale-in groups work the same way. Example: BUY 100 ADAUSD (lot 1) LIMIT at 0.1900 with SL 0.1866 and
            target 0.1922 — price touches 0.1899, the paper trade opens at 0.1899.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Exits</strong> — every 30 s the app checks the live price (Delta mark price; NSE
            from your Zerodha session, else the last stored price) against the stop and the target and closes at that
            price. Drag the SL / Target lines on the chart as usual. Paper trades are not trailed. In the example,
            price at 0.1924 closes it at 0.1924: (0.1924 − 0.1899) × 100 × 1 × ₹/$ ≈ ₹21. No fees or slippage are
            modelled.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Where it shows</strong> — the Trades page (PAPER chip; ✕ closes it at the live
            price), the chart&apos;s position pill (PAPER tag), and Telegram messages tagged 📝 PAPER. If you hold a real
            and a paper position on the same symbol, the chart shows the real one.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Kept apart from real money</strong> — paper is never in the header Open P&amp;L
            (its tooltip lists it separately), Dashboard positions and P&amp;L, Analytics, the daily-loss cap, the
            liquidation guard or the Telegram reports. The kill switch and loss cap do not block paper entries, and a
            paper position never blocks, merges with or closes a real one on the same symbol.</>} />
        </ListItem>
      </List>
      <H3>2 · NBUY / NSHORT / BBUY / BSHORT — trigger levels</H3>
      <P>
        Four <em>trigger-level</em> tools that arm a price level (no SL/Target capture). Pick the tool, click
        the chart to drop the level (or drag the round chip on the right axis to move one). They populate the
        Dashboard's <strong>Pending Manual Trigger Watch</strong>, and with that panel's <strong>Auto-Execute</strong>{' '}
        switch ON a hit places a protected order (auto-derived ISL/Target from 15-minute candles). Conditions:
      </P>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Tool</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Fires when</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Use when</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell><strong>NBUY</strong> (normal buy)</TableCell><TableCell>price ≤ level</TableCell><TableCell>Buy the dip at support.</TableCell></TableRow>
          <TableRow><TableCell><strong>NSHORT</strong> (normal short)</TableCell><TableCell>price ≥ level</TableCell><TableCell>Short the rip at resistance.</TableCell></TableRow>
          <TableRow><TableCell><strong>BBUY</strong> (breakout buy)</TableCell><TableCell>price ≥ level</TableCell><TableCell>Breakout long — only once price breaks <em>up</em> through the level.</TableCell></TableRow>
          <TableRow><TableCell><strong>BSHORT</strong> (breakdown short)</TableCell><TableCell>price ≤ level</TableCell><TableCell>Breakdown short — once price breaks <em>down</em> through the level.</TableCell></TableRow>
        </TableBody>
      </Table>
      <P>
        Trigger levels show as horizontal dashed lines with a side chip; same-side levels (the two buy or the
        two short) clear together when one fires. Hotkeys: <strong>n</strong> then <strong>b</strong>/<strong>s</strong>{' '}
        arms NBUY/NSHORT; <strong>Shift+B</strong> then <strong>b</strong>/<strong>s</strong> arms BBUY/BSHORT.
      </P>
      <P>
        The axis label of a trigger line says <strong>· waiting</strong> when the level was set on the side it
        fires on (for example an NBUY placed below the price): it stays quiet until price moves away and comes
        back through it, so setting a level never fires it on the spot.
      </P>
      <H id="level-option">Option strategy on a trigger level (BTCUSD / ETHUSD on Delta · PAPER or LIVE)</H>
      <P>
        On a Delta <strong>BTCUSD</strong> or <strong>ETHUSD</strong> chart, every set trigger level gets a{' '}
        <strong> ⚡</strong> chip next to its × in the Levels row. Click it to attach an option strategy: when the
        level fires, the app opens that strategy <em>instead of</em> trading the coin itself. One level does one
        thing — an attached level never also places the futures order. A new attachment starts on <strong>LIVE</strong>{' '}
        (real orders on your Delta account, placed in price-capped slices — see the Options Workspace guide&apos;s
        &quot;How LIVE orders are placed&quot;); one click on <strong>PAPER</strong> makes it simulated (filled from
        the live order book). Without an active Delta API key it starts on PAPER and LIVE is disabled.
      </P>
      <P>
        <strong>LIVE.</strong> The dialog shows the max loss at your size and your Delta balance, and the button reads
        <strong> Attach — LIVE (real orders)</strong> in red — that click is the confirmation; there is nothing to type.
        When the level fires, the app reads your Delta wallet again and places nothing if the wallet can't be read
        or doesn't cover the strategy's margin plus 10% — you get a Telegram either way. Protective BUY legs are
        placed first, and the order book must be deep enough for your size. The kill switch and daily-loss cap
        apply as for any order. LIVE attachments show <strong>LIVE</strong> in red on the chip, the axis label
        and the Dashboard.
      </P>
      <P>
        The dialog has two halves. On the left you pick the <strong>strategy</strong> (strategies that agree with
        the level come first: bullish for NBUY / BBUY, bearish for NSHORT / BSHORT; neutral and volatility plays
        are listed under "Other — not directional"), the <strong>expiry</strong> (Today, Tomorrow, This week,
        This month, plus "skip if under N hours" — default 6 h, 6 h, 48 h, 7 days — which rolls to the next
        expiry when the chosen one is too close), the <strong>lots</strong> and an optional <strong>max ₹
        risk</strong> (lots are cut to fit it; if one lot does not fit, the strategy is not opened). On the right
        is a preview <strong>"if it fired at this level"</strong>: today's option chain moved to the level price,
        with legs, credit or debit, max profit, max loss, break-evens and the payoff. Strikes and prices are worked
        out again at the moment the level fires, so the real position can differ a little from the preview.
      </P>
      <P>
        <strong>An exact expiry date.</strong> Under the chips, every live expiry is listed as a date with the time
        left (<strong>M</strong> marks the monthly). Picking one fixes it: it is never rolled, and if it has settled,
        or is closer than "skip if under" (at least 30 min) when the level fires, the strategy is not opened. A chip
        goes back to the rolling choice. <strong>Edit strikes / legs</strong> turns the template into your own legs (up
        to 6): BUY/SELL, CE/PE, strike (− / + or pick from the listed strikes), lots, ✕ to remove, + Buy/Sell
        Call/Put, <strong>+ Add wings</strong> and Shift ◀ ▶. Choose how the strikes are used when it fires:{' '}
        <strong> Exact strikes</strong> (the default with a fixed date) trades those strikes and does not open if one
        is no longer listed and priced; <strong>Same distance from ATM</strong> keeps each leg's distance, in listed
        strikes, from the ATM at the fire, so the shape follows the price. <strong>Reset to …</strong> goes back to the
        template. The axis label, its tooltip and the Dashboard then show the date (e.g. <em>6 Nov✎</em>, ✎ = your legs).
      </P>
      <P>
        <strong>Exits — whichever comes first.</strong> A ₹ target and a ₹ stop on the basket's total P&amp;L,
        and/or a target price and a stop price on the coin itself (target above / stop below the level for NBUY
        and BBUY; the reverse for NSHORT and BSHORT). At least one stop is required. The dialog starts with a ₹
        target of half the max profit and a ₹ stop of the credit (credit trades) or half the debit (debit trades).
        Both ₹ amounts are <strong>net</strong>: what you receive after closing at the bid / ask and the entry and
        exit fees. Nothing trails the stop automatically — once the basket is open, drag its stop on the basket
        chart (Options Workspace) to lock in profit. The basket is also closed before expiry (default 60 min for
        Today / Tomorrow, 6 h for weekly / monthly).
      </P>
      <P>
        <strong>When it fires.</strong> Only with the Dashboard's <strong>Auto-Execute</strong> switch ON. With it
        OFF the level only alerts, the attachment stays set, and the chip, axis label and dialog say{' '}
        <strong> paused</strong>. A leg with no bid / ask, or a bid-ask spread wider than the limit (default 15%),
        is not traded. The app keeps retrying for up to 10 minutes while price stays within 0.5% of the level, then
        gives up and tells you why on Telegram. If only some legs fill, it is never retried; the basket is kept for
        you to close or adopt. If the NBUY and BBUY (or NSHORT and BSHORT) levels fire together, only one opens,
        and an attachment on the other level is dropped. The opened basket is named after the strategy and the
        level (for example "Bull Put (credit) · NB 76,500"), shows a <strong>⚡ Level</strong> chip in Options →
        Monitor, and closes with "Price target hit", "Price stop hit" or "Profit lock hit" when one of those exits
        fires.
      </P>

      <H id="order-confirm">OrderConfirmDialog</H>
      <P>
        Opens on every Execute click (whether from a chart trigger, an alert row, or Quick-Trade). Two
        tabs:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Direct</strong> — single-leg order. Symbol, side, qty, SL, Target. Trade Sizing strip at the bottom shows Required ₹ / Available ₹ as a coverage bar. <strong>Place as</strong> PAPER / LIVE at the bottom (opens on LIVE) — see <em>LIVE or PAPER</em> above.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Strategy</strong> — multi-leg basket (options only). Pick from suggested baskets (Long Straddle, Iron Condor, etc.); each leg gets its own row with a per-leg fill chip after Confirm.</>} />
        </ListItem>
      </List>
      <GuideCallout kind="info">
        For options the dialog auto-suggests strike + expiry based on Delta target. You can override but
        the dialog warns about deep-OTM or near-expiry contracts.
      </GuideCallout>

      <H id="scale-in">Multi-entry scale-in (averaging-down)</H>
      <P>
        Set <strong>Settings → Trading Preferences → Entry Splitting (N)</strong> to 1–5 (default 3). With
        N&gt;1 a chart BUY/SHORT becomes a group of N resting entry levels that share one SL and one Target.
        You click N entry levels (then SL, then Target); the auto-computed total quantity is split by the{' '}
        <em>doubling rule</em> — the last leg is half the total, the first two are equal, and each leg from
        the third onwards is double the size of the previous. Effect: every fill after the first one
        approximately doubles the cumulative position, which recenters the blended average toward the
        midpoint of (prior cumulative avg, new fill level).
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<>Split per N: <strong>2</strong> → ½,½ · <strong>3</strong> → ¼,¼,½ · <strong>4</strong> → ⅛,⅛,¼,½ · <strong>5</strong> → 1/16,1/16,⅛,¼,½.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<>Ordering — BUY: E1 &gt; E2 &gt; … with SL below the last entry, Target above E1 (mirrored for SHORT). The confirm dialog blocks out-of-order captures.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<>Entry 1 may be LIMIT (rests) or MARKET (fires now); E2..EN always rest. The exchange never sees the resting levels — the app fires a MARKET order when each is reached and averages into one position.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<>The confirm dialog's <em>"If filled through each level"</em> table shows cumulative qty, blended average, loss-if-SL and profit-if-target at every stage before you Place.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<>Exits — ISL is active from the first fill. Target hit at any stage exits fully and cancels the remaining entries; an SL breach closes the filled qty and cancels the rest. SL-breach confirmation timing comes from Settings → Trailing SL.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<>Each level renders as a draggable dashed line labelled E1/E2/…; the Dashboard's Pending Entry Watch shows every leg with an E{'{'}i{'}'}/{'{'}N{'}'} badge and PENDING/TRIGGERED status. Changing N later never re-shapes an already-placed group.</>} />
        </ListItem>
      </List>

      <H id="overview">OverView trend meter & Bkground tint</H>
      <P>
        <strong>OverView</strong> (toggle before BB) pins a compact dashboard to the chart's top-left with two
        half-circle speedometers — one for the chart timeframe, one for its mapped Higher Timeframe (Strategies →
        Timeframe Mapping). Each needle is a consolidated <strong>-100..+100</strong> score; below it the
        constituents are shown in a two-column grid, each a colored shape (▲ up · ▼ down · ◆ neutral · – no data),
        with constituents you've switched OFF dimmed + struck-through.
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>14 constituents (R123):</strong> EMAs, AvgCloud, CPR, C-POC, H-POC, RelStr, ChgN%, MACD, JHM, WR%, plus BO_D, BO_W, Vol and AV_Band. Most are instant (per-bar) reads; <strong>BO_D / BO_W / Vol / AV_Band are latched</strong> — they hold a +1/−1 vote until the opposite event (breakout-line touch / volume spike) flips it.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<>Score = <em>weighted</em> average of the ON constituents that have a signal (±1 each) × 100. Zones: Strong Buy ≥ +60, Probable Buy +20..+60, Neutral ±20, Probable Short −20..−60, Strong Short ≤ −60.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Contributors:</strong> in Chart Settings → Indicators → OverView, each of the 14 has an on/off toggle (off = weight 0 = excluded from the score everywhere) plus a numeric weight. Turn ON just one (with RockBand active) to inspect exactly how it colors the bars.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<>The score is <strong>candle-type independent</strong> (computed on raw OHLC) and CPR/POC use the standard base period (≤15m → Daily, ≤4h → Weekly, &gt;4h → Monthly) — identical to what the Scan / Backtest / Auto-Trade use, so the chart matches them. <strong>Changing any constituent / weight / threshold shifts the live RockBand signals.</strong></>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Bkground</strong> (toggle after OverView) tints each candle&rsquo;s background. Chart Settings &rarr; Indicators &rarr; Bkground picks how the colour is worked out: <strong>Overview_CTF</strong> (default) by the sign of the combined chart-TF + HTF trend score; <strong>Overview_HTF</strong> by the RockBand zone colour of the HTF bar containing the candle; or <strong>CLH midline (HTF)</strong> by the slope of the CLH Band&rsquo;s higher-timeframe middle line &mdash; bullish where it falls, bearish where it rises. Colours and opacity are yours; the opacity is scaled down on the dark theme so the same number reads the same on both.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<>Move the crosshair over any candle to see that candle's OverView in a tooltip — the score + zone for both the chart timeframe and its HTF (with up/down counts).</>} />
        </ListItem>
      </List>

      <H id="chart-loading">Chart loading</H>
      <P>
        Opening any symbol — or restarting the app — restores the full chart setup you last used on <em>any</em>{' '}
        symbol: <strong>layout, candle type, timeframe, indicators and the applied strategy</strong>. Remembered
        automatically (saved as one snapshot), no toggle to manage. On the very first load (nothing remembered yet)
        a default set is on: OverView, Bkground, AvgCloud, C-POC, LR_HAMA_Band, RelStrength, ChgN% and MACD.
      </P>
      <P>
        <strong>Strategy applied from a chart icon.</strong> When you open a chart by clicking the Chart icon
        from a <em>strategy-aware</em> place — a Trades row, a Recent / Parked alert, an Alerts-page row, a
        Pending AutoTrade Execution row, or the Strategies page — the chart opens with <strong>that strategy's
        overlay already applied</strong> (in addition to the right timeframe). Icons with no strategy context
        (Watchlist, HVN, Pending Manual Entry / Trigger) open on your remembered overlay as usual.
      </P>
      <P>
        <strong>Remembered zoom.</strong> The chart also remembers the <strong>number of candles you last had in
        view, per layout</strong> (single / 2-split / 4-quad): scroll-zoom to the candle count you like and every new
        symbol you open in that layout starts at the same zoom. Reset it any time with <em>Chart Settings → Candles →
        Reset remembered zoom</em>. (The three manual zoom fields that used to live in Chart Settings were removed in
        favour of this.)
      </P>

      <H id="replay">Bar Replay mode</H>
      <P>
        Turn it on with the <strong>R</strong> chip in the toolbar's <strong>Levels</strong> group, or the{' '}
        <GuideKeycap>r</GuideKeycap> hotkey. When ON, future bars are masked and a playback strip appears
        at the bottom. Click Play → bars reveal one at a time. Every indicator recomputes as the playhead
        advances so you see exactly what the indicator <em>actually said</em> at decision time, not the
        repainted retrospective view.
      </P>
      <P>
        Four ways to set the playhead start point:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Click on the chart</strong> — playhead jumps to the clicked candle. (Only when no drawing or order tool is armed; with a tool armed the click belongs to the tool.)</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Date/time picker</strong> — the calendar icon on the replay bar, for a precise jump.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>The scrubber</strong> — drag to any bar in the loaded range.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Trades page Replay deep-link</strong> — opens the chart with playhead at the trade's entry candle.</>} />
        </ListItem>
      </List>
      <P>
        The replay bar carries: restart, step back, play/pause, step forward, a <strong>step size</strong>{' '}
        selector (1 Bar / 1 Hour / 4 Hour / 1 Day / 1 Week — converted to bars at the chart's interval),
        jump-to-end, the scrubber, a <strong>speed</strong> selector (1×, 2×, 3×, 4×, 5×, 7×, 8×), the date
        picker and close. Drag the grip to move the bar; double-click it to recentre. Multi-pane layouts
        share the playhead so all charts move together.
      </P>
      <P>
        <strong>Data: Broker / Free</strong> on the replay bar loads a deeper history window for replay only —
        Broker re-pages your exchange, Free pulls Binance (crypto) / Yahoo (NSE). Use it when you want to
        replay further back than the chart has loaded. Normal left-drag history loading is paused during
        replay, because prepending bars would shift the indices out from under the playhead.
      </P>
      <P>
        <strong>What you can do while replaying.</strong> Marking the chart up is the point, so{' '}
        <strong>HVN lines</strong> and <strong>Rect zones</strong> work normally, and you can pan and zoom
        freely — the view only follows the playhead if you were already parked at the right edge. What is
        blocked is anything that arms or modifies a <em>real</em> order: NB / NS / BB / BS trigger levels,
        BUY / SHORT capture, Add ISL / Add Tgt and the Strategy / Outlook builders. Those chips dim while
        replay is on and say so if you press them — a stop taken off a frozen historical bar would be a
        live order at a price that is months old.
      </P>
      <P>
        Switching <strong>timeframe</strong> mid-replay keeps your place: the playhead re-anchors to the same
        moment on the new interval (and leaves replay if that moment is outside the history loaded there).
        Switching symbol, exchange or layout leaves replay and returns the chart to live.
      </P>
      <P>
        During replay the <strong>LTP line</strong> stays visible, anchored to the <strong>actual raw close of the
        last revealed bar</strong> — regardless of the candle type you're viewing (Heikin-Ashi, CLH, etc.) — so the
        price reference always matches the real market close at that point in history.
      </P>

      <H id="order-flow-ribbon">Order Flow ribbon</H>
      <P>
        Bottom-of-chart histogram strip. One bar per chart-interval candle, coloured by a 7-state palette
        derived from taker-buy ratio + trend. Bar height is proportional to taker volume (R69a) so visually
        busy candles stand tall.
      </P>
      <P>
        The 7 states (left = most bullish, right = most bearish):
      </P>
      <Box component="pre" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem', p: 1.25, my: 1, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 0.75 }}>{`
Bulls in control · Bulls holding · Bulls fading · Neutral · Bears fading · Bears holding · Bears in control
   deep green       med green       pale green     grey    pale red       med red          deep red
`}</Box>
      <P>
        Hover any bar for the breakdown: buy vol / sell vol / total / buyPct / windowBias. See
        <OpenInApp to="/app-guide#order-flow" label="Order Flow" /> for full mechanics.
      </P>
      <GuideCallout kind="tip">
        Since R79, the bottom-left live OF panel's <em>1m / 5m / 15m</em> buttons align to the current candle
        bucket — so the panel's bias pill always matches the ribbon's rightmost bar. <em>30s</em> stays
        rolling (no sub-minute candle to align to).
      </GuideCallout>

      <H id="hvn">HVN levels — marking your S/R (with cross alerts)</H>
      <P>
        HVN ("High-Volume Node") is a horizontal price level you draw across the chart for visual S/R. Click the
        HVN chip in the toolbar, then click on the chart at the price level. The line draws across all panes for
        that symbol, is <strong>draggable</strong> (drag to re-price) and <strong>deletable</strong> (hover → ×),
        and persists per-user. The tool is <em>one-shot</em> — after a single capture it disarms, so a stray second
        click won't drop another level; click the HVN chip again to add the next.
      </P>
      <P>
        Each HVN you save is mirrored onto the Dashboard's <strong>Price Level Watch</strong>. When live price{' '}
        <strong> crosses</strong> an HVN level the platform raises a <strong>cross alert (and a Telegram message)</strong>{' '}
        so you're notified at your marked S/R — it does <strong>not</strong> place any order. (HVNs also still feed the
        Order Flow absorption detector: a one-sided volume burst into an HVN that price doesn't break shows an
        absorption tag — S-abs for support, R-abs for resistance — in the OF panel.)
      </P>

      <H id="fno-overlay">FNO overlays</H>
      <P>
        For NSE/NFO underlying symbols (NIFTY, BANKNIFTY, individual stocks with options), enable FNO
        overlays in Chart Settings:
      </P>
      <GuideCallout kind="warning" title="Option polling is config-gated">
        The background option-chain poller <strong>idles until enabled</strong> in Settings → Options Polling,
        so these NSE/NFO FNO overlays (OI walls / max-pain / ATM) <strong>do not auto-refresh</strong> while it's
        off. (Delta crypto option features on this page use the live Delta chain and are unaffected.) See the
        <OpenInApp to="/app-guide#fno-options" label="FNO / Options" /> section.
      </GuideCallout>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>OI walls</strong> — coloured horizontal bands at the top 3 call-OI and put-OI strikes for the chosen expiry.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Max-pain line</strong> — strike where total option-writer pain is minimised (often a magnet near expiry).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>ATM marker</strong> — current at-the-money strike highlighted.</>} />
        </ListItem>
      </List>

      <H id="option-baskets">Option baskets — click-to-build (Delta)</H>
      <P>
        On a Delta crypto symbol you can build a multi-leg option basket directly from the chart. Pick a basket
        type from the <strong>Build</strong> row — Straddle, Strangle, Call/Put credit or debit spread, and{' '}
        <strong> Iron Condor / Iron Fly</strong> — then click the chart at the level(s) it asks for; each click
        snaps to the nearest listed strike and the legs are assembled for you.
      </P>
      <P>
        <strong>Outlook → basket (Strategy Architect on the chart).</strong> The purple <strong>Outlook</strong> row
        adds five view-driven buttons — <em>Won't fall ↓ / Won't rise ↑ / Break ↑ / Break ↓ / In range</em>. Click
        one, then click the chart to set your level (two clicks — lower then upper — for <em>In range</em>). The
        engine then ranks the best option baskets for that view from the live chain, with payoff, win-probability,
        an <em>"if your view is just proven"</em> P&amp;L, the net Greeks and a plain-English rationale per basket
        (cards or sortable grid) — and <em>only</em> baskets that profit when your view is right are shown (losers are
        strictly discarded). Each card auto-sizes the lots from your option budget × leverage (editable) and places
        directly: <strong>📝 Paper trade</strong> (simulated) or <strong>🟢 Place LIVE</strong> (confirm → real orders),
        both monitored. This is the same engine as the Options-AT <OpenInApp to="/app-guide#options-at" label="Strategy Architect" />.
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Review dialog</strong> — opens with a live payoff curve, max-profit / max-loss / break-evens, margin, R:R, win-probability and the per-leg list. The lots auto-size from the underlying's option budget × leverage (credit → OPT Sell, debit → OPT Buy); adjust each leg's strike with +/− steppers and the lots — everything recomputes live. No order is placed until you place it.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Next-expiry protection (Iron Condor / Iron Fly)</strong> — a Same / Next-expiry toggle rolls the protective BUY wings to a later expiry (slower theta decay on the hedge) while the short legs stay near. The payoff then shows two curves: solid = near expiry, dashed = far expiry.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Strike-availability fallback</strong> — if a picked strike isn't listed on the chosen day (Delta's near-expiry ladders are narrow), the builder automatically moves those legs to the nearest day that lists them and shows a note explaining the change.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Paper trade / Place LIVE</strong> — <strong>📝 Paper</strong> opens a simulated position (filled at the legs' last price, no broker order); <strong>🟢 Place LIVE</strong> (after a confirm dialog showing legs / qty / margin / max-loss) places all legs through your daily-loss / kill-switch gate (protective BUY legs first). Either way the basket monitor manages it (take-profit / stop on the net premium, and a clean roll-off before the near expiry settles for multi-expiry baskets), tagged PAPER or LIVE.</>} />
        </ListItem>
      </List>
      <GuideCallout kind="info" title="Expiries shown are live only">
        Only expiries whose settlement (12:00 UTC for Delta) is still ahead are offered — an expiry that has
        already settled today is never selectable.
      </GuideCallout>

      <H id="strategy-overlays">Strategy overlays</H>
      <P>
        When you have one of the in-house strategies enabled for a symbol, its signal markers appear on the
        chart at the bar where the signal fired. ▲ for BUY/COVER, ▼ for SHORT/SELL. Hover for the strategy
        + strength + entry/SL/target.
      </P>

      <H id="hotkeys">Hotkeys</H>
      <P>
        Press <GuideKeycap>?</GuideKeycap> on any page to see the full hotkey overlay.
        Chart-specific:
      </P>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Key</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell><GuideKeycap>1</GuideKeycap>–<GuideKeycap>9</GuideKeycap></TableCell><TableCell>Switch the focused pane's interval — 1=1m, 2=3m, 3=5m, 4=15m, 5=30m, 6=1H, 7=4H, 8=1D, 9=1W.</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>b</GuideKeycap></TableCell><TableCell>Toggle Bollinger overlay.</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>c</GuideKeycap></TableCell><TableCell>Toggle AvgCloud overlay.</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>p</GuideKeycap></TableCell><TableCell>Toggle CPR overlay.</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>r</GuideKeycap></TableCell><TableCell>Toggle Bar Replay mode.</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>n</GuideKeycap> then <GuideKeycap>b</GuideKeycap> / <GuideKeycap>s</GuideKeycap></TableCell><TableCell>Arm the NBUY / NSHORT trigger-level tool.</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>Shift</GuideKeycap>+<GuideKeycap>B</GuideKeycap> then <GuideKeycap>b</GuideKeycap> / <GuideKeycap>s</GuideKeycap></TableCell><TableCell>Arm the BBUY / BSHORT breakout trigger-level tool.</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>↑</GuideKeycap> / <GuideKeycap>↓</GuideKeycap></TableCell><TableCell>Step to the previous / next symbol in the (sorted, filtered) symbol panel.</TableCell></TableRow>
        </TableBody>
      </Table>
      <P>
        The toolbar also carries a <strong>Wick ✓ / Wick ✕</strong> toggle (just after the candle-type buttons) to
        show or hide candle wicks — body-only view hides the high/low shadows. The choice is remembered.
      </P>

      <P>
        <strong>CLN — Candle + Linear regressioN.</strong> Every other candle type replaces the bar with a
        smoothed one. CLN is the exception: it draws the <strong>real</strong> market candle and a{' '}
        <strong>wickless linear-regression body</strong> on top of it, together, so you can see the trend the
        regression is fitting <em>and</em> the price action underneath it in one read. The real candle recedes
        to a pale ghost; the regression body sits over it, opaque.
      </P>
      <P>
        The rule that makes it useful: <strong>colour comes from the regression, never from the bar</strong>.
        A red bar inside a rising regression prints bullish. When a large real candle carries a small
        opposite-direction regression body, that is the chart telling you the move has not yet turned the
        trend — which is the whole reason to look at CLN rather than at a smoothed type. Because that rule is
        absolute, a running strategy's candle colours are suppressed while CLN is selected (its markers,
        signals and tooltip are untouched). The <strong>Wick</strong> toggle controls the real candle only;
        the regression layer never has wicks. Regression length is in Chart Settings → Candles (default 14),
        along with an off-by-default outline that marks regression bodies printed on thin volume.
      </P>
      <P>
        CLN is the one type that does not change bar geometry, so indicators, the crosshair, the OHLC legend
        and the LTP line all keep reading true prices — and scans and backtests treat CLN exactly as Regular.
      </P>
      <P>
        <strong>LR chip — the same regression body over any candle type.</strong> The <strong>LR</strong> chip in
        the Candle group draws the CLN body over whatever type is selected (Regular, HA, CSH, CSL, CLH). It is
        always the regression of the <em>real</em> opens and closes, so it is the same body on every type and
        the same one CLN_Break, Gandiv and LR_Cross read (LR_Cross alerts when a close crosses its top or bottom;
        see the LR_Cross Strategy section). By default the candles under it are ghosted the CLN way (pale,
        coloured by the regression, strategy candle colours paused); turn that off in Chart Settings →
        LR body (CLN) to keep the candles' own colours with the body drawn on top. CLN itself is simply
        Regular + LR + ghost: with CLN selected the chip shows ✓, and switching it off returns to Regular. The
        chip applies to every pane of a split or quad layout, like the candle type.
      </P>
      <P>
        <strong>Important fraction</strong> (Chart Settings → Candles). Builds every candle type (Reg, HA, CLH,
        CLN) from only the <strong>last part</strong> of each bar&apos;s time. With <em>1/3</em> on a 30-minute
        chart, each candle&apos;s open, high, low and volume come from its last 10 minutes; its close is the
        bar&apos;s real close. HA and CLH are then worked out from those bars, and for CLN both the pale candle
        and the regression body follow them. <em>1</em> (the default) means normal candles, exactly as before.
        Choices are 1/2 up to 1/10.
      </P>
      <P>
        It only changes how the candles look. Indicators, strategies, scans, alerts, orders and P&amp;L keep
        using the real bars, the price line and badges stay real, and the OHLC legend shows the real prices
        with a second line, <em>Fraction ¹⁄₃ O … H … L …</em>, for the bar under the crosshair. That legend line
        is the only marker that a fraction is on. The small real high / low / close ticks (switched with the
        Wick toggle) show where the real bar went. It is not a tested edge: a study of 40 Delta symbols found
        that the last part of a bar did not predict the next move better than the whole bar did.
      </P>
      <P>
        A fraction candle needs each bar&apos;s inside (finer bars), fetched from the same source as the chart.
        Where the window does not split into whole finer bars (for example 1/2 of a 15-minute bar) it is rounded
        to whole minutes and the legend says so. On NSE / MCX the last part is measured in <em>trading</em> time
        (the last third of a 09:15–15:30 day is from 13:25). Bars older than the finer data the venue still
        serves stay normal candles, drawn faded, and the legend says from which date fraction candles start.
        A still-forming bar stays normal until its last part begins. A 1-minute chart, option panes and
        synthetic symbols always draw normal candles.
      </P>
      <P>
        <strong>HTF Band</strong> (chip, Bands &amp; Levels). The higher timeframe mapped to your chart
        (Strategies → Timeframe Mapping) drawn as a ribbon of its CLN regression bodies behind the candles:
        one step per higher-timeframe bar. Colour: when <em>both</em> lines step up from the previous
        higher-timeframe bar it is teal (rising), when both step down it is red (falling); otherwise it
        follows that bar&apos;s regression direction (teal rising, red falling). Fill and lines share the colour.
        It works with any candle type and draws nothing on 1W. <em>Chart Settings → HTF Band</em> sets its
        opacity and whether the still-forming higher-timeframe bar is included (drawn lighter and dashed,
        because it changes until that bar closes). It used to be a switch inside the CLN settings.
      </P>
      <P>
        <strong>P-Zone</strong> (chip, Bands &amp; Levels). A profit-zone <em>reference</em>: two amber levels
        beyond the HTF Band, in the direction the band is moving. <em>Zone 1</em> (solid) sits 2.5 and{' '}
        <em>Zone 2</em> (dashed) 4 times the average bar range (ATR over 50 chart bars) past the band&apos;s edge:
        above price while the band rises, below it while it falls, and nothing at all while the band is mixed
        or flat. They are set once at each higher-timeframe close and do not move until the next one. Each
        step uses the higher-timeframe bar that has already closed, so a step sits one higher-timeframe period
        to the right of the HTF Band step it came from, and past steps show the zones exactly as they were
        known at the time. The OHLC legend shows the zones for the bar under the crosshair, and the axis shows
        PZ1 / PZ2.
      </P>
      <P>
        It is <strong>not a tested signal</strong>. In a study of 220 Delta symbols, booking part of a trade at
        Zone 1 was no better than booking at a random profitable moment, and a touch of Zone 1 did not reliably
        mean the move was tiring. Nothing in the app (scans, strategies, alerts, orders) acts on it. The
        distances, colour and fill are in <em>Chart Settings → P-Zone</em>. 2.5 and 4 are the study&apos;s
        values; adjusting them by eye until they match past turns is curve-fitting. Nothing is drawn on 1W,
        which has no higher timeframe.
      </P>
      <P>
        <strong>MM on the higher timeframe.</strong> <em>Chart Settings → MM</em> has two switches:
        <em> Chart timeframe</em> (the usual staircase) and <em>Higher timeframe</em> (the same staircase built
        from the mapped higher timeframe, with dashed edges and its own opacity). A higher-timeframe mark only
        exists once its bar has closed, so each of its stairs starts at the first candle after that close.
        It never stretches back across the higher-timeframe bar, where it would show a level before it existed.
      </P>

      <GuideExample title="A typical chart workflow">
        Open BANKNIFTY on the 5m chart. Notice the AvgCloud is green (uptrend). Press <GuideKeycap>b</GuideKeycap>
        — Bollinger overlay appears; price is hugging the lower band. Click the green <strong>BUY</strong> button
        → cursor changes → click the chart at your entry level, then click your SL, then your Target.
        OrderConfirmDialog opens with the auto-computed quantity (your investment amount ÷ price, editable) and
        the Risk:Reward. Confirm → the deferred entry rests and shows in the Dashboard's Pending Manual Entry Watch.
      </GuideExample>

      <H id="r115-cond3">Strategy markers, Add ISL / Add Tgt &amp; the unified tooltip</H>
      <GuideCallout kind="info" title="RockBand signals = candle colour + HTF zone (V2)">
        Live RockBand fires off the chart-TF candle colour (Strong-zone) and the mapped HTF background zone —
        see the <OpenInApp to="/app-guide#strategy-rockband" label="RockBand Strategy" /> section. The earlier
        AvgCloud "Cond 1 / 2 / 3" entry machine is retired for RockBand. The <strong>BOLev_D / BOLev_W</strong>{' '}
        no-wick "Cond 3" shapes still <em>draw</em> on the chart as a BOLev study, but they are <strong>not</strong>{' '}
        fed into the RockBand strategy.
      </GuideCallout>
      <P>
        <strong>Plotshape labels:</strong> RockBand BUY / SHORT arrows use short <strong>B</strong> /
        <strong>S</strong> labels for readability against the candle stream; position-state colouring (green
        LONG / red SHORT / neutral FLAT) shows what the strategy is holding.
      </P>
      <P>
        <strong>Unified crosshair tooltip:</strong> one always-on floating box aggregates OHLC, the TF / HTF
        OverView scores + zones, BOLev_D / BOLev_W study state, the RockBand position, and on a signal candle a
        bold accent line naming the signal in plain English (e.g. "BUY · High — Green candle, HTF Strong-Buy").
      </P>
      <P>
        <strong>Add ISL / Add Tgt buttons:</strong> placed after SHORT in the chart toolbar (purple / teal pills).
        Click to arm, then click anywhere on the chart's price area to set the ISL or Target on the open trade
        for the active symbol. Buttons are disabled when no open trade exists. Uses the same single open-trade
        rule per symbol — all splits under that trade share one ISL and one Target.
      </P>
      <P>
        <strong>Auto-ISL / Auto-Target:</strong> every fresh RockBand BUY / SHORT (and Sniper signal)
        auto-derives an ISL = <em>min/max of the previous 2 CLH candles ± Breach Buffer</em> and Target = <em>entry ± N × risk</em>{' '}
        (N from Settings → Target tab). The values are persisted on the alert and seed the Trade row on creation.
        Manual overrides via Trade Management or the chart-click buttons remain available.
      </P>
      <P>
        <strong>Auto-Trade direction gate:</strong> when Auto-Trade is enabled but the per-symbol Auto Long
        (for BUY) or Auto Short (for SHORT) toggle is OFF on the Instruments page, the alert is logged with an
        orange <em>AUTO SKIPPED · AL OFF / AS OFF</em> chip in the Alerts list, and no order is placed. Enable
        the flag to allow auto-execution. Exits always close existing positions regardless of these flags.
      </P>
      <P>
        <strong>Symbol panel:</strong> sort dropdown next to the search box (Name ▲▼ and % Change ▲▼ — % variants
        sort once the symbol-list endpoint supplies per-symbol pct data). Clear-X icon inside the search field
        when text is present.
      </P>
      <P>
        <strong>Chart Settings dialog:</strong> drag by the title bar to reposition. Edits to an existing
        template auto-save 600 ms after the last change — a small <em>✓ Saved</em> / <em>Saving…</em>{' '}
        indicator sits next to the Save button so you know edits persist live.
      </P>
    </>
  ),
  searchIndex: [
    { heading: 'Layouts',           anchor: 'layouts',           body: 'layouts single 2-split 4-quad split quad multi pane options workspace' },
    { heading: 'Intervals',         anchor: 'intervals',         body: 'intervals 1m 5m 15m 1h 4h 1d 1w composite 45m 90m' },
    { heading: 'Chart Settings',    anchor: 'chart-settings',    body: 'chart settings dialog display overlays flow fno triggers' },
    { heading: 'Indicator overlays',anchor: 'overlay-categories',body: 'indicator overlays trend volatility volume reference comparison' },
    { heading: 'Order capture',     anchor: 'order-capture',     body: 'order capture nbuy nshort bbuy bshort trigger entry chart' },
    { heading: 'OrderConfirmDialog',anchor: 'order-confirm',     body: 'order confirm dialog direct strategy basket sizing' },
    { heading: 'PAPER chart trades', anchor: 'paper-trade',      body: 'paper trade simulated live place as buy short practice no broker isSimulation' },
    { heading: 'Multi-entry scale-in', anchor: 'scale-in',       body: 'multi entry scale in averaging down doubling rule split quantity N entries pyramid SL target stages' },
    { heading: 'OverView trend meter', anchor: 'overview',       body: 'overview trend meter speedometer gauge score strong probable buy short zones background bkground tint htf timeframe ema avgcloud cpr poc relstrength chgn macd crosshair tooltip candle hover' },
    { heading: 'Chart loading',        anchor: 'chart-loading',  body: 'chart loading last used indicators timeframe layout sticky default first load remember on click load' },
    { heading: 'Bar Replay mode',   anchor: 'replay',            body: 'bar replay mode playhead date picker recompute' },
    { heading: 'Order Flow ribbon', anchor: 'order-flow-ribbon', body: 'order flow ribbon strip seven state palette taker bias' },
    { heading: 'HVN levels',        anchor: 'hvn',               body: 'hvn high volume node level sr support resistance absorption' },
    { heading: 'FNO overlays',      anchor: 'fno-overlay',       body: 'fno options overlay oi walls max pain atm marker' },
    { heading: 'Option baskets — click-to-build', anchor: 'option-baskets', body: 'option basket build iron condor iron fly straddle strangle credit debit spread next expiry protection payoff review approve delta' },
    { heading: 'Strategy overlays', anchor: 'strategy-overlays', body: 'strategy overlay sniper rockband dailyblast markers' },
    { heading: 'Hotkeys',           anchor: 'hotkeys',           body: 'hotkeys keyboard shortcuts interval bollinger cloud replay hvn' },
  ],
};

export default Section_charts;
